import React, { useState } from 'react';
import { Puzzle, User, UserProgress, Challenge } from '../types';
import { X, Swords, ArrowRight, Sparkles, AlertCircle, Copy, Check } from 'lucide-react';
import { COBALT_BLUE } from '../constants';

interface FaithDuelManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  progress: UserProgress;
  puzzle: Puzzle | null;
  onOpenSupport: () => void;
  onGoToLogin: () => void;
  onChallengeAccepted: (challenge: Challenge, targetPuzzle: Puzzle) => void;
  onCreateChallenge: (puzzle: Puzzle) => void;
  allPuzzles: Puzzle[];
}

export const FaithDuelManageModal: React.FC<FaithDuelManageModalProps> = ({
  isOpen,
  onClose,
  user,
  progress,
  puzzle,
  onOpenSupport,
  onGoToLogin,
  onChallengeAccepted,
  onCreateChallenge,
  allPuzzles
}) => {
  const [pasteInput, setPasteInput] = useState('');
  const [errorText, setErrorText] = useState('');

  if (!isOpen) return null;

  const isGuest = !user || user.isGuest;
  const isSupporter = progress.isSponsored;

  const handleParseAndAccept = () => {
    setErrorText('');
    const trimmedInput = pasteInput.trim();
    if (!trimmedInput) {
      setErrorText('Please paste an invitation code or link first.');
      return;
    }

    let payload = '';

    // Let's write a powerful robust parser for the input string!
    // Try to find if user pasted a link like "https://...?duel=..."
    try {
      if (trimmedInput.includes('?duel=') || trimmedInput.includes('&duel=')) {
        const queryIndex = trimmedInput.indexOf('duel=');
        const remainingStr = trimmedInput.substring(queryIndex + 5).trim();
        // Extract up to next separator or newline
        const endOfPayload = remainingStr.match(/^([a-zA-Z0-9+/=]+)/);
        if (endOfPayload && endOfPayload[1]) {
          payload = endOfPayload[1];
        } else {
          payload = remainingStr.split(/[&\s\n]/)[0];
        }
      } else if (trimmedInput.includes('Code:')) {
        // If they pasted the combined format with "Code:" line
        const codeLines = trimmedInput.split('\n');
        const codeLine = codeLines.find(line => line.trim().startsWith('Code:') || line.trim().match(/^[a-zA-Z0-9+/=]{15,}$/));
        if (codeLine) {
          if (codeLine.includes('Code:')) {
            payload = codeLine.replace('Code:', '').trim();
          } else {
            payload = codeLine.trim();
          }
        }
      } else {
        // Fallback: look for clean base64 string matching block inside the text
        const base64Regex = /[a-zA-Z0-9+/=]{20,}/g;
        const matches = trimmedInput.match(base64Regex);
        if (matches && matches.length > 0) {
          payload = matches[matches.length - 1]; // pick the longest or last matching string
        } else {
          payload = trimmedInput;
        }
      }

      // Strip any whitespace, semicolons or quotes from payload
      payload = payload.replace(/['"\s;]/g, '').trim();

      if (!payload) {
        throw new Error('Could not find challenge payload in pasted text.');
      }

      // Try to decode Base64
      let decodedStr = '';
      try {
        decodedStr = decodeURIComponent(escape(atob(payload)));
      } catch {
        decodedStr = atob(payload);
      }

      const decodedChallenge = JSON.parse(decodedStr) as Challenge;
      
      if (!decodedChallenge || !decodedChallenge.puzzleId) {
        throw new Error('Invalid challenge structure.');
      }

      // Find the puzzle
      const matchedPuzzle = allPuzzles.find(p => p.id === decodedChallenge.puzzleId);
      if (!matchedPuzzle) {
        setErrorText('This challenge is for a puzzle that does not exist in this version of the app.');
        return;
      }

      // Successfully parsed!
      onChallengeAccepted(decodedChallenge, matchedPuzzle);
      setPasteInput('');
      onClose();

    } catch (e) {
      console.error("Decode error", e);
      setErrorText('❌ Could not parse invitation. Please paste a valid Faith Duel code or link.');
    }
  };

  return (
    <div className="fixed inset-0 z-[115] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-lg rounded-[2.5rem] p-8 md:p-10 shadow-2xl animate-in zoom-in duration-300 max-h-[90vh] flex flex-col overflow-y-auto z-10">
        
        {/* Header */}
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-300 hover:text-gray-500 transition cursor-pointer">
          <X className="w-6 h-6" />
        </button>

        <header className="flex items-center gap-3.5 mb-6">
          <div className="bg-rose-100 p-3.5 rounded-2xl text-rose-600">
            <Swords className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-2xl font-cinzel font-bold text-gray-900 leading-none">Faith Duel</h2>
            <p className="text-xs font-bold text-rose-600 uppercase tracking-widest mt-1">Iron sharpens iron</p>
          </div>
        </header>

        {/* Dynamic Content by User Level */}
        {isGuest ? (
          /* GUEST CONTENT */
          <div className="space-y-6 text-center py-4">
            <div className="p-5 bg-amber-50 rounded-2xl border border-amber-100/50 flex flex-col items-center">
              <Sparkles className="w-8 h-8 text-amber-500 mb-3" />
              <p className="text-sm font-semibold text-amber-900 leading-relaxed">
                Guest players are limited to 1 puzzle per category. Create a player profile and support our mission to unlock Faith Duels and all 100+ beautiful puzzles!
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => {
                  onClose();
                  onGoToLogin();
                }}
                className="flex-1 bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-blue-800 transition active:scale-95 cursor-pointer text-sm"
              >
                Create Profile (2 Puzzles)
              </button>
              <button
                onClick={() => {
                  onClose();
                  onOpenSupport();
                }}
                className="flex-1 bg-rose-600 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-rose-700 transition active:scale-95 cursor-pointer text-sm"
              >
                Become a Supporter
              </button>
            </div>
          </div>
        ) : !isSupporter ? (
          /* REGISTERED PROFILE BUT NOT ACTIVATED */
          <div className="space-y-6 text-center py-4">
            <div className="p-5 bg-blue-50/50 rounded-2xl border border-blue-100 flex flex-col items-center">
              <Sparkles className="w-8 h-8 text-blue-500 mb-3" />
              <p className="text-sm font-semibold text-blue-900 leading-relaxed">
                Shalom, <strong className="text-blue-700">{user?.username}</strong>! Standard profiles get 2 default puzzles per category. Supporting the mission unlocks the full library of 100+ puzzles and enables creation and participation of Faith Duels!
              </p>
            </div>

            <button
              onClick={() => {
                onClose();
                onOpenSupport();
              }}
              className="w-full bg-rose-600 text-white font-bold py-4.5 rounded-2xl shadow-xl hover:bg-rose-700 transition active:scale-95 cursor-pointer text-base"
            >
              Support the Mission to Unlock
            </button>
          </div>
        ) : (
          /* FULLY ACTIVATED USER SCREEN */
          <div className="flex-1 flex flex-col">
            {puzzle ? (
              /* Create a Duel Screen only (no tabs) */
              <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-2">
                <div className="p-5 bg-slate-50 border border-slate-100 rounded-2xl flex items-center gap-4 text-left">
                  <div className="w-14 h-14 bg-white border border-slate-200 rounded-xl overflow-hidden shrink-0 flex items-center justify-center">
                    <img src={puzzle.imageUrl} alt={puzzle.title} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">Active Puzzle Target</p>
                    <h4 className="font-bold text-gray-800 text-base">{puzzle.title}</h4>
                    <p className="text-xs text-gray-400">{puzzle.book} {puzzle.chapter}:{puzzle.verse}</p>
                  </div>
                </div>

                <p className="text-xs text-slate-500 leading-relaxed text-left font-semibold">
                  To set up a challenge, you will type a clue to a verse of the Bible and select the correct bible book, chapter and verse answer. They will solve it to unlock this puzzle!
                </p>

                <button
                  onClick={() => {
                    onClose();
                    onCreateChallenge(puzzle);
                  }}
                  className="w-full hover:bg-rose-700 text-white font-bold py-4 rounded-2xl shadow-lg active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 mt-2"
                  style={{ backgroundColor: 'rgb(225, 29, 72)' }}
                >
                  <Sparkles className="w-5 h-5" />
                  Design Duel
                </button>
              </div>
            ) : (
              /* Accept a Duel Screen only (no tabs) */
              <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300 font-semibold">
                <p className="text-xs text-slate-500 leading-relaxed text-left">
                  Paste your Faith Duel invitation code or link here.
                </p>

                <div className="relative">
                  <textarea
                    value={pasteInput}
                    onChange={(e) => {
                      setPasteInput(e.target.value);
                      setErrorText('');
                    }}
                    placeholder="Paste Faith Duel invitation code or link here..."
                    className="w-full text-xs font-mono p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none h-28 resize-none leading-relaxed text-slate-700 font-semibold shadow-inner"
                  />
                </div>

                {errorText && (
                  <div className="p-3.5 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 text-xs font-semibold flex items-start gap-2 animate-shake">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{errorText}</span>
                  </div>
                )}

                <button
                  onClick={handleParseAndAccept}
                  className="w-full text-white font-bold py-4 rounded-2xl shadow-lg active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                  style={{ backgroundColor: COBALT_BLUE }}
                >
                  <Swords className="w-5 h-5" />
                  Accept Duel & Solve Quiz
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
